import moment from 'moment';

import { TTreeOption } from '@/common/components/redesigned/TreeMultiselect/types';
import { MONTH_NAMES } from '@/common/constants/months';
import { PERIODS_SEPARATORS } from '@/common/constants/periods';
import { TSelectOption } from '@/common/types/select';

import { HALF_YEAR_LABELS } from '../constants/halfYear';
import { QUARTER_LABELS } from '../constants/quarters';
import { getCurrentYear } from './year';

export const getYearsFilterOptions = (fromYear: number, toYear: number = getCurrentYear()): TSelectOption<string>[] => {
  const years: TSelectOption<string>[] = [];

  for (let year = toYear; year >= fromYear; year--) {
    years.push({
      value: year.toString(),
      label: year.toString(),
    });
  }

  return years.reverse();
};

export const getHalfYearFilterOptions = (year: number): TSelectOption<string>[] => {
  return [1, 2].map((half) => ({
    value: `${year}${PERIODS_SEPARATORS.halfYear}${half}`, // 2024-H1
    label: `${HALF_YEAR_LABELS[half - 1]} ${year}`, // 1 полугодие 2024
  }));
};

export const getQuarterFilterOptions = (year: number): TSelectOption<string>[] => {
  return [1, 2, 3, 4].map((quarter) => ({
    value: `${year}${PERIODS_SEPARATORS.quarter}${quarter}`, // 2024-Q1
    label: `${QUARTER_LABELS[quarter - 1]} ${year}`, // 1 квартал 2024
  }));
};

export const getMonthFilterOptions = (year: number): TSelectOption<string>[] => {
  return MONTH_NAMES.map((name, index) => {
    const monthNumber = index + 1;
    return {
      value: `${year}${PERIODS_SEPARATORS.month}${monthNumber}`, // 2024-M1
      label: `${name} ${year}`, // янв 2024
    };
  });
};

export const getYearHalfQuarterMonthTree = (fromYear: number, toYear: number = getCurrentYear()): TTreeOption[] => {
  return getYearsFilterOptions(fromYear, toYear).map((yearOption) => {
    const year = Number(yearOption.value);
    const halfYearOptions = getHalfYearFilterOptions(year).map((halfOption, halfIndex) => {
      const quarterOptions = getQuarterFilterOptions(year)
        .slice(halfIndex * 2, halfIndex * 2 + 2)
        .map((quarterOption, quarterIndex) => {
          const monthOptions = getMonthFilterOptions(year).slice(
            (halfIndex * 2 + quarterIndex) * 3,
            (halfIndex * 2 + quarterIndex) * 3 + 3,
          );
          return { ...quarterOption, options: monthOptions };
        });
      return { ...halfOption, options: quarterOptions };
    });
    return { ...yearOption, options: halfYearOptions };
  });
};

export const getYearMonthTree = (fromYear: number, toYear: number = getCurrentYear()): TTreeOption[] => {
  return getYearsFilterOptions(fromYear, toYear).map((yearOption) => {
    return {
      ...yearOption,
      options: getMonthFilterOptions(Number(yearOption.value)),
    };
  });
};

export const getYearMonthFlatOptions = (
  fromYear: number,
  toYear: number = getCurrentYear(),
): TSelectOption<string>[] => {
  const result: TSelectOption<string>[] = [];
  for (let year = toYear; year >= fromYear; year--) {
    result.push(...getMonthFilterOptions(year));
  }
  return result;
};

const getMonthLastDate = (year: number, monthNumber: number): Date => {
  return moment({ year, month: monthNumber - 1, day: 1 })
    .endOf('month')
    .toDate();
};

const getFirstMondayInMonth = (year: number, monthNumber: number): Date => {
  const monthStart = moment({ year, month: monthNumber - 1, day: 1 });
  const firstIsoWeekDay = monthStart.isoWeekday();
  const daysToMonday = firstIsoWeekDay === 1 ? 0 : 8 - firstIsoWeekDay;
  return monthStart.add(daysToMonday, 'days').toDate();
};

export const getWeekFilterOptions = (year: number, monthNumber: number): TSelectOption<string>[] => {
  const monthLabel = MONTH_NAMES[monthNumber - 1];
  const monthLastDate = getMonthLastDate(year, monthNumber);
  const firstWeekStart = new Date(year, monthNumber - 1, 1);
  const firstMonday = getFirstMondayInMonth(year, monthNumber);
  const weekStarts: Date[] = [firstWeekStart];

  if (firstMonday.getDate() !== 1) {
    weekStarts.push(firstMonday);
  }

  let cursor = new Date(firstMonday);
  while (cursor <= monthLastDate) {
    cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 7);
    if (cursor <= monthLastDate) {
      weekStarts.push(cursor);
    }
  }

  return weekStarts.map((weekStart, weekIndex) => {
    return {
      value: `${year}${PERIODS_SEPARATORS.month}${monthNumber}${PERIODS_SEPARATORS.week}${weekIndex + 1}`,
      label: `неделя с ${moment(weekStart).format('DD')} ${monthLabel}. ${year}`,
    };
  });
};

export const getYearMonthWeekTree = (fromYear: number, toYear: number = getCurrentYear()): TTreeOption[] => {
  return getYearsFilterOptions(fromYear, toYear).map((yearOption) => {
    const year = Number(yearOption.value);
    const monthOptions = getMonthFilterOptions(year).map((monthOption, monthIndex) => {
      return {
        ...monthOption,
        options: getWeekFilterOptions(year, monthIndex + 1),
      };
    });

    return {
      ...yearOption,
      options: monthOptions,
    };
  });
};
